import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ITableState } from '../../../base';
import {
    IFetchTableDataByIdAction,
    IFetchTableNextPageAction,
    IHandlePathChangedAction,
    ISetRowsDataByIdAction,
    ISetTableStateByIdAction,
    IUpdateTableRowById,
    IUpsertTableRowAction,
} from './interface';
import { saveTableStateToSession, updateTableDataByRow } from '../../../common';
import { createInitialState } from './utils';

// Define ITablesSlice interface
export interface ITablesSlice {
    [key: string]: {
        state: ITableState;
        data: Array<Record<string, string | number | null>>;
    };
}

const initialState = createInitialState();

export const sliceName = 'tables';

export const tablesSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        fetchTableDataById: (
            state,
            action: PayloadAction<IFetchTableDataByIdAction>
        ) => {
            const { tableId, page } = action.payload;
            const table = state[tableId];
            if (table) {
                if (page > 1) {
                    table.state.isLoadingMore = true;
                } else {
                    table.state.isLoading = true;
                }
            }
        },
        fetchTableNextPage: (
            _state,
            _action: PayloadAction<IFetchTableNextPageAction>
        ) => {},
        handlePathChanged: (
            _state,
            _action: PayloadAction<IHandlePathChangedAction>
        ) => {},
        setRowsDataById: (
            state,
            action: PayloadAction<ISetRowsDataByIdAction>
        ) => {
            const { tableId, data, page } = action.payload;
            const table = state[tableId];
            if (table) {
                if (page > 1) {
                    const currentData = table.data;
                    const updatedData = [...currentData, ...data];
                    table.state.isLoadingMore = false;
                    table.data = updatedData;
                } else {
                    table.state.isLoading = false;
                    table.data = data;
                }
            }
        },
        setTableStateById: (
            state,
            action: PayloadAction<ISetTableStateByIdAction>
        ) => {
            const { tableId, tableState } = action.payload;
            const table = state[tableId];
            if (table) {
                table.state = tableState;
                saveTableStateToSession(tableId, tableState);
            }
        },
        updateTableRowById: (
            state,
            action: PayloadAction<IUpdateTableRowById>
        ) => {
            const { tableId, newTableRow } = action.payload;
            const table = state[tableId];
            console.log('[updateTableRowById] Updating table row:', {
                tableId,
                newTableRow,
                tableExists: !!table,
                dataLength: table?.data?.length,
            });
            if (table) {
                const rowIndex = table.data.findIndex(
                    (row) => String(row.id) === String(newTableRow.id)
                );
                console.log('[updateTableRowById] Found row at index:', rowIndex);
                if (rowIndex !== -1) {
                    const oldRow = table.data[rowIndex];
                    table.data[rowIndex] = {
                        ...table.data[rowIndex],
                        ...newTableRow,
                    };
                    console.log('[updateTableRowById] Updated row:', {
                        old: oldRow,
                        new: table.data[rowIndex],
                    });
                }
            }
        },
        removeTableRowsById: (
            state,
            action: PayloadAction<{
                tableId: string;
                rowIds: Array<string | number | null>;
            }>
        ) => {
            const { tableId, rowIds } = action.payload;
            const table = state[tableId];
            if (table) {
                table.data = table.data.filter(
                    (row) => !rowIds.includes(row.id)
                );
            }
        },
        upsertTableRow: (
            state,
            action: PayloadAction<IUpsertTableRowAction>
        ) => {
            const { tableId, row } = action.payload;
            const table = state[tableId];
            if (table) {
                const existingIndex = table.data.findIndex(
                    (r) => r.id === row.id
                );
                if (existingIndex !== -1) {
                    table.data[existingIndex] = row;
                } else {
                    table.data = [row, ...table.data];
                }
            }
        },
    },
});

export const tablesActions = tablesSlice.actions;
export default tablesSlice.reducer;
